import React, { useState, useRef, useEffect } from 'react';
import { shuffle } from 'lodash';
import styles from '../Vol1.module.css';
import { useNavigate } from 'react-router-dom';
import { getAudioUrl, getImageUrl } from '../services/r2Service';
import { FaPlay, FaPause, FaForward, FaBackward, FaRandom, FaRedo, FaPlus } from 'react-icons/fa';
import { Song } from '../components/types';
import { useQueue } from '../contexts/QueueContext';

interface Vol1Props {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
}

const songs: Song[] = [
  { id: 'v1-house-on-the-hill', audioFile: getAudioUrl('megapackv1/house on the hill 03.30.23.mp3'), coverImage: getImageUrl('houseonthehill.png'), title: 'House on the Hill', volume: 1 },
  { id: 'v1-bangaroid-jr-jones', audioFile: getAudioUrl('megapackv1/BangaroidJrJones5142020.mp3'), coverImage: getImageUrl('bangaroid.png'), title: 'Bangaroid Jr. Jones', volume: 1 },
  { id: 'v1-fully-loaded', audioFile: getAudioUrl('megapackv1/fully loaded 04.13.21.mp3'), coverImage: getImageUrl('fullyloaded.png'), title: 'Fully Loaded', volume: 1 },
  { id: 'v1-i-did-you-wrong', audioFile: getAudioUrl('megapackv1/i did you wrong 04.02.21.mp3'), coverImage: getImageUrl('didyouwrong.png'), title: 'I did you wrong', volume: 1 },
  { id: 'v1-dream', audioFile: getAudioUrl('megapackv1/dream 01.21.21.mp3'), coverImage: getImageUrl('dream.jpg'), title: 'Dream', volume: 1 },
  { id: 'v1-lock-and-pick', audioFile: getAudioUrl('megapackv1/Lock and Pick 03.11.23.mp3'), coverImage: getImageUrl('lockandpick.png'), title: 'Lock and Pick', volume: 1 },
  { id: 'v1-hollow-night', audioFile: getAudioUrl('megapackv1/Hollow Night 10.22.22.mp3'), coverImage: getImageUrl('hollownight.png'), title: 'Hollow Night', volume: 1 },
  { id: 'v1-equilibrium', audioFile: getAudioUrl('megapackv1/Equilibrium 04.12.22.mp3'), coverImage: getImageUrl('Equilibrium.png'), title: 'Equilibrium', volume: 1 },
  { id: 'v1-hurts-so-bad', audioFile: getAudioUrl('megapackv1/Hurts So Bad 03.26.22.mp3'), coverImage: getImageUrl('hurtssobad.png'), title: 'Hurts So Bad', volume: 1 },
  { id: 'v1-kota', audioFile: getAudioUrl('megapackv1/kota 10.08.20.mp3'), coverImage: getImageUrl('kota.png'), title: 'Kota', volume: 1 },
  { id: 'v1-bop', audioFile: getAudioUrl('megapackv1/Bop 04.29.22.mp3'), coverImage: getImageUrl('bop.png'), title: 'Bop', volume: 1 },
  { id: 'v1-dilla', audioFile: getAudioUrl('megapackv1/Dilla 03.30.22.mp3'), coverImage: getImageUrl('dilla.png'), title: 'Dilla', volume: 1 },
  { id: 'v1-code', audioFile: getAudioUrl('megapackv1/Code 05.08.22.mp3'), coverImage: getImageUrl('code.png'), title: 'Code', volume: 1 },
  { id: 'v1-clouds', audioFile: getAudioUrl('megapackv1/clouds 07.14.20.mp3'), coverImage: getImageUrl('clouds.png'), title: 'Clouds', volume: 1 },
  { id: 'v1-someone-give-this-man-a-dolla', audioFile: getAudioUrl('megapackv1/someone give this man a dollar 05.18.20.mp3'), coverImage: getImageUrl('dolla.png'), title: 'Someone Give This Man a Dolla', volume: 1 },
];

const Vol1: React.FC<Vol1Props> = ({ searchTerm, setSearchTerm }) => {
  const audioRef = useRef<HTMLAudioElement>(new Audio());
  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [filteredSongs, setFilteredSongs] = useState(songs);
  const [isPlaying, setIsPlaying] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const [shuffleEnabled, setShuffleEnabled] = useState(false);
  const [shuffledSongs, setShuffledSongs] = useState(songs);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const navigate = useNavigate();
  const prevImageUrlRef = useRef<string | null>(null);
  const { addToQueue, isQueueActive } = useQueue();
  useEffect(() => {
    if (isQueueActive) { audioRef.current.pause(); setIsPlaying(false); }
  }, [isQueueActive]);

  const handleNextTapeClick = () => { navigate('/Vol2'); };
  const handlePrevTapeClick = () => { navigate('/Vol5'); };

  const handleShuffle = () => {
    const audioElement = audioRef.current;
    const currentSong = shuffleEnabled ? shuffledSongs[currentSongIndex] : filteredSongs[currentSongIndex];
    const currentSongIndexInFiltered = filteredSongs.findIndex(song => song.title === currentSong.title);
    const currentPlaybackTime = audioElement.currentTime;

    if (shuffleEnabled) {
        setShuffledSongs(filteredSongs);
        setCurrentSongIndex(currentSongIndexInFiltered);
    } else {
        const upcomingSongs = shuffle(filteredSongs.filter((_, index) => index !== currentSongIndexInFiltered));
        setShuffledSongs([currentSong, ...upcomingSongs]);
        setCurrentSongIndex(0);
    }
    setShuffleEnabled(!shuffleEnabled);
    setRepeat(false);
    setTimeout(() => {
        audioElement.currentTime = currentPlaybackTime;
        if (isPlaying) audioElement.play().catch(() => {});
    }, 0);
  };

  const handleRepeat = () => {
    const audioElement = audioRef.current;
    const currentSongs = shuffleEnabled ? shuffledSongs : filteredSongs;
    const currentSong = currentSongs[currentSongIndex];
    const currentSongIndexInFiltered = filteredSongs.findIndex(song => song.title === currentSong.title);
    const currentPlaybackTime = audioElement.currentTime;

    setRepeat(!repeat);
    setShuffleEnabled(false);
    setShuffledSongs(filteredSongs);
    setCurrentSongIndex(currentSongIndexInFiltered);
    setTimeout(() => {
        audioElement.currentTime = currentPlaybackTime;
        if (isPlaying) audioElement.play().catch(() => {});
    }, 0);
  };

  const handleSongEnd = () => {
    if (repeat) {
        audioRef.current.currentTime = 0;
        audioRef.current.play();
    } else {
        const nextIndex = (currentSongIndex + 1) % (shuffleEnabled ? shuffledSongs.length : filteredSongs.length);
        setCurrentSongIndex(nextIndex);
    }
  };

  useEffect(() => {
    audioRef.current.addEventListener('ended', handleSongEnd);
    return () => { audioRef.current.removeEventListener('ended', handleSongEnd); };
  }, [repeat, shuffleEnabled, currentSongIndex]);

  useEffect(() => {
    const audioElement = audioRef.current;
    const handleLoadedMetadata = () => { setDuration(audioElement.duration); };
    const handleTimeUpdate = () => { setCurrentTime(audioElement.currentTime); };
    audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    audioElement.addEventListener('timeupdate', handleTimeUpdate);
    return () => {
      audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audioElement.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, []);

  useEffect(() => {
    const currentSongs = shuffleEnabled ? shuffledSongs : filteredSongs;
    const audioElement = audioRef.current;
    if (currentSongs.length > 0 && audioElement.src !== currentSongs[currentSongIndex].audioFile) {
        audioElement.src = currentSongs[currentSongIndex].audioFile;
        if (!shuffleEnabled) { audioElement.currentTime = 0; }
        if (isPlaying) { audioElement.play().catch(() => {}); }
    } else if (currentSongs.length === 0) {
        audioElement.pause();
        audioElement.currentTime = 0;
        setIsPlaying(false);
    }
  }, [currentSongIndex, shuffleEnabled, filteredSongs, shuffledSongs]);

  useEffect(() => {
    if (searchTerm === '') {
      setFilteredSongs(songs);
      setShuffledSongs(songs);
    } else {
      const filtered = songs.filter(song => song.title.toLowerCase().includes(searchTerm.toLowerCase()));
      setFilteredSongs(filtered);
      setShuffledSongs(filtered);
    }
    setCurrentSongIndex(0);
  }, [searchTerm]);

  useEffect(() => {
    return () => { audioRef.current.pause(); audioRef.current.currentTime = 0; };
  }, []);

  const formatTime = (time: number) => {
    const floorTime = Math.floor(time);
    const minutes = Math.floor(floorTime / 60);
    const seconds = '0' + (floorTime - minutes * 60);
    return minutes + ':' + seconds.substr(-2);
  };

  const handleTogglePlay = () => {
    if (isPlaying) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setIsPlaying(!isPlaying);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    audioRef.current.volume = newVolume;
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    audioRef.current.currentTime = newTime;
  };

  const changeSong = (delta: number) => {
    const currentSongs = shuffleEnabled ? shuffledSongs : filteredSongs;
    const nextIndex = (currentSongIndex + delta + currentSongs.length) % currentSongs.length;
    setCurrentSongIndex(nextIndex);
  };

  const selectSong = (title: string) => {
    const index = filteredSongs.findIndex(song => song.title === title);
    if (index > -1) {
      setCurrentSongIndex(index);
      setIsPlaying(true);
      audioRef.current.play().catch(() => {});
    }
  };

  useEffect(() => {
    const currentSongs = shuffleEnabled ? shuffledSongs : filteredSongs;
    const currentSong = currentSongs[currentSongIndex];
    const currentImageUrl = currentSong?.coverImage;
    if (prevImageUrlRef.current !== currentImageUrl) {
      setIsImageLoading(true);
      prevImageUrlRef.current = currentImageUrl;
    }
  }, [currentSongIndex, shuffleEnabled, filteredSongs, shuffledSongs]);

  return (
    <div className={styles.container}>
      <h1 className={styles.volumeTitle}>Volume 1</h1>
      <input className={styles['search-bar']} type='text' placeholder='Search for a song...' value={searchTerm} onChange={event => setSearchTerm(event.target.value)} />
      <div className={styles.albumCoverContainer}>
        {filteredSongs.length > 0 ? (
          (() => {
            const coverUrl = shuffleEnabled ? shuffledSongs[currentSongIndex]?.coverImage : filteredSongs[currentSongIndex]?.coverImage;
            const isVideo = coverUrl?.match(/\.(mp4|mov|webm)$/i);
            return isVideo ? (
              <video src={coverUrl} className={`${styles.albumCover} ${isImageLoading ? styles.loading : ''}`} autoPlay loop muted playsInline onLoadedData={() => setIsImageLoading(false)} />
            ) : (
              <img src={coverUrl} alt='Cover' className={`${styles.albumCover} ${isImageLoading ? styles.loading : ''}`} onLoad={() => setIsImageLoading(false)} />
            );
          })()
        ) : (
          <img src={getImageUrl('nophoto.jpg')} alt='No Results' className={styles.albumCover} />
        )}
      </div>

      <div className={styles.audioControls}>
        <button onClick={handleShuffle} className={styles.button} title={shuffleEnabled ? 'Disable Shuffle' : 'Enable Shuffle'} disabled={filteredSongs.length === 0}>
          <FaRandom className={shuffleEnabled ? styles.activeIcon : styles.icon} />
        </button>
        <button onClick={() => changeSong(-1)} className={styles.button} title="Previous" disabled={filteredSongs.length === 0}>
          <FaBackward className={styles.icon} />
        </button>
        <button onClick={handleTogglePlay} className={`${styles.button} ${styles.playButton}`} title={isPlaying ? 'Pause' : 'Play'} disabled={filteredSongs.length === 0}>
          {isPlaying ? <FaPause className={styles.icon} /> : <FaPlay className={styles.icon} />}
        </button>
        <button onClick={() => changeSong(1)} className={styles.button} title="Next" disabled={filteredSongs.length === 0}>
          <FaForward className={styles.icon} />
        </button>
        <button onClick={handleRepeat} className={styles.button} title={repeat ? 'Disable Repeat' : 'Enable Repeat'} disabled={filteredSongs.length === 0}>
          <FaRedo className={repeat ? styles.activeIcon : styles.icon} />
        </button>
      </div>

      <div className={styles.playerControls}>
        <div className={styles.trackTitle}>
          {filteredSongs.length > 0
            ? (shuffleEnabled ? shuffledSongs[currentSongIndex]?.title : filteredSongs[currentSongIndex]?.title)
            : 'No songs found'}
        </div>
        <div className={styles.timeControls}>
          <div className={styles.timeRow}>
            <span className={styles.timeText}>{formatTime(currentTime)}</span>
            <input type="range" min="0" max={duration} value={currentTime} onChange={handleTimeChange} className={styles.timeSlider} disabled={filteredSongs.length === 0} />
            <span className={styles.timeTextRight}>{formatTime(duration)}</span>
          </div>
        </div>
        <div className={styles.volumeSliderWrapper}>
          <input type="range" min="0" max="1" step="0.01" value={volume} onChange={handleVolumeChange} className={styles.volumeSlider} />
        </div>
        <div className={styles.volumeText}>Volume</div>
        <div style={{ height: '2rem' }} />
        <div className={styles.tapeControls}>
          <button onClick={handleNextTapeClick}>Next Tape</button>
          <button onClick={handlePrevTapeClick}>Previous Tape</button>
        </div>
      </div>

      <div className={styles.tracklist}>
        <h2 className={styles.tracklistLabel}>Tracklist</h2>
        {filteredSongs.length > 0 ? (
          filteredSongs.map(song => (
            <div key={song.id} className={styles.tracklistItem}>
              <span onClick={() => selectSong(song.title)} style={{ flex: 1, cursor: 'pointer' }}>{song.title}</span>
              <button onClick={(e) => { e.stopPropagation(); addToQueue(song); }} style={addQueueBtnStyle} title="Add to Queue">
                <FaPlus />
              </button>
            </div>
          ))
        ) : (
          <p className={styles.tracklistLabel}>No songs match your search.</p>
        )}
      </div>
    </div>
  );
};

const addQueueBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: '#815353', cursor: 'pointer',
  padding: '4px 8px', fontSize: '0.8rem', transition: 'color 0.2s',
};

export default Vol1;
